//@ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useChat } from "../../contexts/ChatContext";
import { useCategories } from "../../contexts/CategoryContext";
import { useSubCategories } from "../../contexts/SubCategoryContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import {
  Plus,
  Search,
  MessageSquare,
  LogOut,
  Menu,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  LogIn,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import toast from "react-hot-toast";
import { getCategoryIcon } from "../../utils/getCategoryIcon";
import SubCategorySelectionModal from "./SubCategorySelectionModal";
import { useTranslation } from "react-i18next";
import AstriaRingLogo from "../astria/AstriaRingLogo";
import { getRegionLabel } from "../../utils/regions";
import AstriaNav from "../astria/AstriaNav";
import { useAstriaCopy } from "../astria/copy";
import { hasActiveSubscription } from "../../utils/subscription";
import { isTabLocked } from "../../utils/planAccess";
import {
  ASTRIA_COUNTRY_CATEGORIES,
  getVisibleCategories,
} from "../../utils/categoryVisibility";
import { localizedField, pickLocalizedFields } from "../../utils/localizedField";
import { useAppLanguage } from "../../utils/appLanguage";



// Jp kyusei & timing tab routing
function resolveJapanDashboardPath(
  name: string | null | undefined,
): string | null {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (
    lower.includes("kyusei") ||
    lower.includes("viral") ||
    lower.includes("omamori")
  ) {
    return "/astria-japan/kyusei";
  }
  if (lower.includes("timing")) {
    return "/astria-japan/timing";
  }
  return null;
}

// Astria GCC V2 — 6-tab emotional check-in dashboard routing.
function resolveGCCV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria GCC V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("timing")) return "/astria-gcc-v2/timing";
  if (lower.includes("kyusei")) return "/astria-gcc-v2/kyusei";
  if (lower.includes("companion")) return "/astria-gcc-v2/companion";
  if (lower.includes("emotional"))
    return "/astria-gcc-v2/emotional-intelligence";
  if (lower.includes("personality")) return "/astria-gcc-v2/personality-engine";
  return null;
}

// Astria Philippines V2 — 6-tab emotional check-in dashboard routing.
function resolvePhilippinesV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Philippines V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-philippines-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-philippines-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-philippines-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-philippines-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-philippines-v2/guidance-path";
  if (lower.includes("note")) return "/astria-philippines-v2/your-note";
  return null;
}

// Astria Indonesia V2 — 6-tab emotional check-in dashboard routing.
function resolveIndonesiaV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Indonesia V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-indonesia-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-indonesia-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-indonesia-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-indonesia-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-indonesia-v2/guidance-path";
  if (lower.includes("note")) return "/astria-indonesia-v2/your-note";
  return null;
}

// Astria Vietnam V2 — 6-tab emotional check-in dashboard routing.
function resolveVietnamV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Vietnam V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-vietnam-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-vietnam-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-vietnam-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-vietnam-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-vietnam-v2/guidance-path";
  if (lower.includes("note")) return "/astria-vietnam-v2/your-note";
  return null;
}

// Astria Vietnam — 5-lane real Tử Vi/lunar-day/compatibility/phong
// thủy/tarot dashboard routing. Distinct category from "Astria Vietnam
// V2" above (guarded on exact-name match, never collides with it).
function resolveAstriaVietnamDashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Vietnam") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (
    lower.includes("birth chart") ||
    lower.includes("tu vi") ||
    lower.includes("tử vi")
  )
    return "/astria-vietnam/birth-chart";
  if (
    lower.includes("lucky day") ||
    lower.includes("xem ngay") ||
    lower.includes("xem ngày")
  )
    return "/astria-vietnam/lucky-day";
  if (lower.includes("compatibility") || lower.includes("relationship"))
    return "/astria-vietnam/compatibility";
  if (
    lower.includes("space energy") ||
    lower.includes("phong thuy") ||
    lower.includes("phong thủy")
  )
    return "/astria-vietnam/space-energy";
  if (lower.includes("tarot")) return "/astria-vietnam/tarot";
  return null;
}

// Astria Brazil V2 — 6-tab emotional check-in dashboard routing.
function resolveBrazilV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Brazil V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-brazil-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-brazil-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-brazil-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-brazil-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-brazil-v2/guidance-path";
  if (lower.includes("note")) return "/astria-brazil-v2/your-note";
  return null;
}

// Astria Mexico V2 — 6-tab emotional check-in dashboard routing.
function resolveMexicoV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Mexico V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-mexico-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-mexico-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-mexico-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-mexico-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-mexico-v2/guidance-path";
  if (lower.includes("note")) return "/astria-mexico-v2/your-note";
  return null;
}

// Astria India V2 — 7-tab dashboard routing. Astria India V3 reuses the
// exact same dashboard paths/components (see AstriaIndiaV2Shell's version
// toggle) rather than a separate route tree, so it's accepted here too —
// the call site appends a "&v=3" query flag when the V3 category was the
// one actually clicked (see indiaVersionQuery below).
function resolveIndiaV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (
    parentCategoryName !== "Astria India V2" &&
    parentCategoryName !== "Astria India V3"
  )
    return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();

  // Map subcategory names to their dashboard paths
  if (lower.includes("aapka") || lower.includes("note"))
    return "/astria-india-v2/aapka-note";
  if (lower.includes("samay") || lower.includes("pravah"))
    return "/astria-india-v2/samay";
  if (lower.includes("vyaktitva") || lower.includes("darshan"))
    return "/astria-india-v2/vyaktitva";
  if (lower.includes("bhavna") || lower.includes("drishti"))
    return "/astria-india-v2/bhavna";
  if (lower.includes("upay") || lower.includes("marg"))
    return "/astria-india-v2/upay";
  if (lower.includes("vivah") || lower.includes("muhurat"))
    return "/astria-india-v2/vivah";
  if (
    lower.includes("sambandh") ||
    lower.includes("taal") ||
    lower.includes("mel")
  )
    return "/astria-india-v2/sambandh";

  return null;
}

// Astria UK V2 — 9-tab dashboard routing. Strictly scoped to the "Astria UK
// V2" category so it never collides with the legacy "Astria UK" (v1) lane,
// which still routes through the generic default chat. Matched by keyword
// substrings against the subcategory name, same convention as
// resolveIndiaV2DashboardPath above — most-specific keywords first so e.g.
// "MateScan" never falls through to a looser match.
function resolveUKV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria UK V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();

  if (lower.includes("energy match") || lower.includes("energy_match"))
    return "/astria-uk-v2/energy-match";
  if (lower.includes("matescan") || lower.includes("mate scan"))
    return "/astria-uk-v2/matescan";
  if (lower.includes("compatibility")) return "/astria-uk-v2/compatibility";
  if (lower.includes("relationship")) return "/astria-uk-v2/relationship";
  if (lower.includes("companion talk") || lower.includes("companion_talk"))
    return "/astria-uk-v2/companion-talk";
  if (lower.includes("cosmic")) return "/astria-uk-v2/cosmic-uk";
  if (lower.includes("emotional rhythm") || lower.includes("emotional_rhythm"))
    return "/astria-uk-v2/emotional-rhythm";
  if (lower.includes("daily flow") || lower.includes("daily_flow"))
    return "/astria-uk-v2/daily-flow";
  if (lower.includes("zodiac home") || lower.includes("zodiac_home"))
    return "/astria-uk-v2/zodiac-home";
  if (
    lower.includes("zodiac personality") ||
    lower.includes("zodiac_personality")
  )
    return "/astria-uk-v2/zodiac-personality";

  return null;
}

// Astria Mexico — 7-tab dashboard routing. Strictly scoped to the "Astria
// Mexico" category so it never collides with the existing "Astria Mexico V2"
// daily check-in lane, which still routes through the generic default chat.
// Matched by keyword substrings against the subcategory name, same
// convention as resolveUKV2DashboardPath above — most-specific keywords
// first so e.g. "Energy Match" never falls through to a looser match.
function resolveMexicoDashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Mexico") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();

  if (lower.includes("energy match") || lower.includes("energy_match"))
    return "/astria-mexico/energy-match";
  if (lower.includes("compatibility") || lower.includes("compatibilidad"))
    return "/astria-mexico/compatibility";
  if (lower.includes("cosmic message") || lower.includes("cosmic_message"))
    return "/astria-mexico/cosmic-message";
  if (lower.includes("relationship") || lower.includes("relacion"))
    return "/astria-mexico/relationship";
  if (lower.includes("companion talk") || lower.includes("companion_talk"))
    return "/astria-mexico/companion-talk";
  if (lower.includes("daily flow") || lower.includes("daily_flow"))
    return "/astria-mexico/daily-flow";
  if (
    lower.includes("zodiac personality") ||
    lower.includes("zodiac_personality")
  )
    return "/astria-mexico/zodiac-personality";

  return null;
}

// Astria Japan Talk — 1-tab dashboard routing.
function isAstriaJapanTalkName(name: string | null | undefined): boolean {
  if (!name) return false;
  return name.trim().toLowerCase() === "astria japan talk";
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ✅ ONLY DESIGN: Astria palette
  // Dark = Astria palette; light = the original purple sidebar.
  const { theme } = useTheme();
  const isLight = theme === "light";
  const colors = isLight
    ? { amethyst: "#6D4C92", darkAmethyst: "#2D1B44", roseGold: "#E5B8AD", pearl: "#F8F4FF" }
    : { amethyst: "#A78BFF", darkAmethyst: "#221A29", roseGold: "#C8BFD1", pearl: "#F7F3FA" };
  const lightSidebarVars = {
    "--astria-surface": "rgba(255,255,255,0.1)",
    "--astria-surface-soft":
      "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
    "--astria-accent-glow": "rgba(255,255,255,0.12)",
    "--astria-text-primary": "#F8F4FF",
    "--astria-text-secondary": "rgba(248,244,255,0.82)",
    "--astria-accent-primary": "#E9DFFF",
    "--astria-text-secondary": "rgba(248,244,255,0.82)",
    "--astria-accent-primary": "#E9DFFF",
  } as React.CSSProperties;

  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const astriaCopy = useAstriaCopy();

  // Track when to auto-scroll
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // Subcategory selection modal state
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [subCategoriesForModal, setSubCategoriesForModal] = useState([]);
  const [expandedSubCategories, setExpandedSubCategories] = useState<
    Record<string, boolean>
  >({});
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const {
    chats,
    currentChat,
    setCurrentChat,
    startNewChat,
    deleteChat,
    fetchSingleChat,
    setChats, // ✅ keep functionality (used below)
  } = useChat();

  const {
    categories,
    isLoading: categoriesLoading,
    hasFetched: categoriesFetched,
  } = useCategories();
  const { subCategories } = useSubCategories();
  const { user, logout } = useAuth();
  const isAuthenticated = !!user;
  const myLang = useAppLanguage();
  const isTester = user?.roleId === 3;
  // Matches the rule Subscription.tsx and Letter.tsx already use: a record
  // counts as active unless it says "expired" or its end date has passed.
  const isSubscribed = useMemo(() => hasActiveSubscription(user), [user]);

  // Ref for scrolling to active chat
  const activeChatRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track previous chat ID to detect when user switches chats
  const prevChatIdRef = useRef<string | null>(null);

  // ✅ Check if current subcategory is ThaiAstro
  const isThaiAstroSubCategory = useMemo(() => {
    if (!isAuthenticated || !currentChat?.subCategoryId) return false;

    const subCategory = subCategories.find(
      (s) => s.id === currentChat.subCategoryId,
    );
    return (
      subCategory?.name === "ThaiAstro" ||
      subCategory?.name?.toLowerCase().includes("thai astro")
    );
  }, [currentChat, subCategories, isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;

      setIsMobile((prev) => {
        // Close sidebar ONLY when switching desktop → mobile
        if (!prev && mobile) {
          setIsSidebarOpen(false);
        }
        return mobile;
      });
    };

    handleResize(); // run once on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect when user switches chats
  useEffect(() => {
    if (currentChat?.id !== prevChatIdRef.current) {
      setShouldAutoScroll(true);
      prevChatIdRef.current = currentChat?.id || null;
    } else {
      setShouldAutoScroll(false);
    }
  }, [currentChat?.id]);

  useEffect(() => {
    if (!currentChat?.categoryId) return;
    const key = currentChat.subCategoryId
      ? `sub:${currentChat.subCategoryId}`
      : `other:${currentChat.categoryId}`;
    setExpandedSubCategories((prev) =>
      prev[key] ? prev : { ...prev, [key]: true },
    );
    setExpandedCategories((prev) =>
      prev[currentChat.categoryId]
        ? prev
        : { ...prev, [currentChat.categoryId]: true },
    );
  }, [currentChat?.id, currentChat?.categoryId, currentChat?.subCategoryId]);

  // Scroll to active chat
  useEffect(() => {
    if (
      shouldAutoScroll &&
      activeChatRef.current &&
      scrollContainerRef.current
    ) {
      setTimeout(() => {
        activeChatRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
      setShouldAutoScroll(false);
    }
  }, [shouldAutoScroll]);

  // Opening "History & Topics" brings the active chat into view.
  useEffect(() => {
    if (!showHistory) return undefined;
    const id = window.setTimeout(() => {
      activeChatRef.current?.scrollIntoView({ block: "nearest" });
    }, 60);
    return () => window.clearTimeout(id);
  }, [showHistory]);

  useEffect(() => {
    if (user?.roleId === 1) window.location.href = "/admin";
  }, [user, navigate]);

  // if (user?.role === "admin") return null;

  const handleMainPlusClick = () => {
    if (user?.roleId === 3) {
      toast.error(t("access.denied"));
      return;
    }
    // Navigate to sanctuary page
    navigate("/sanctuary", { replace: true });

    // Clear current chat selection
    setCurrentChat(null);

    // Close sidebar on mobile
    if (isMobile) {
      setIsSidebarOpen(false);
      dispatchSidebarState(false);
    }

    // Optional: Show a toast message
    // toast.success(t('sidebar.selectCategory'));
  };

  const handleSubscriptionClick = () => {
    navigate("/journey");

    if (isMobile) {
      setIsSidebarOpen(false);
      dispatchSidebarState(false);
    }
  };

  const handleNewChat = async (categoryId?: string) => {
    try {
      if (categoryId) {
        const category = categories.find((c) => c.id === categoryId);
        if (category) {
          const categoryNameLower = category.name.toLowerCase();
          if (category.name === "Astria Indonesia V3") {
            navigate(`/indomodule?categoryId=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }
          if (
            categoryNameLower.includes("check-mate") ||
            categoryNameLower.includes("checkmate") ||
            categoryNameLower.includes("check mate")
          ) {
            navigate("/checkmate");
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          if (
            categoryNameLower.includes("energymatchv3") ||
            categoryNameLower.includes("energy match v3")
          ) {
            navigate(`/energyMatchV3?categoryId=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          if (
            categoryNameLower.includes("energymatchv2") ||
            categoryNameLower.includes("energy match v2")
          ) {
            navigate(`/energyMatchV2?categoryId=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          if (categoryNameLower.includes("matescan")) {
            navigate(`/matescan?categoryId=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          if (category.name === "Energy Match") {
            if (isTabLocked(user, category.name)) {
              navigate("/lock");
              return;
            }
            navigate(`/energyMatch?category=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          if (categoryNameLower.includes("social compatibility")) {
            navigate(
              user?.hasCompatibilityProfile
                ? "/matches"
                : "/compatibility-form",
            );
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          // Astria Tarot → standalone JP tarot page (shown in every region).
          if (categoryNameLower === "astria tarot") {
            navigate("/jp-tarot");
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          // Route Kyusei Viral / Timing Flow to their standalone dashboards
          // when they are clicked directly as a top-level category (name
          // matched the same way as the subcategory path below, so this
          // works identically whichever level they live at — no code
          // change needed if they move between category/subcategory).
          const directJapanDashboardPath = resolveJapanDashboardPath(
            category.name,
          );
          if (directJapanDashboardPath) {
            navigate(`${directJapanDashboardPath}?categoryId=${categoryId}`);
            if (isMobile) {
              setIsSidebarOpen(false);
              dispatchSidebarState(false);
            }
            return;
          }

          const activeSubCategories = subCategories.filter(
            (sc) => sc.categoryId === categoryId && sc.isActive,
          );

          if (activeSubCategories.length > 0) {
            setSelectedCategory({ id: categoryId, name: category.name });
            setSubCategoriesForModal(
              activeSubCategories.map((sc) => ({
                id: sc.id,
                ...pickLocalizedFields(sc, ["name", "description"]),
                icon: sc.icon,
                locked: isTabLocked(user, sc.name),
              })),
            );
            setShowSubCategoryModal(true);
            return;
          }
        }
      }

      const newChat = await startNewChat(categoryId);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);

      setShouldAutoScroll(true);
      if (isMobile) {
        setIsSidebarOpen(false);
        dispatchSidebarState(false);
      }
    } catch (error) {
      console.error("Failed to start new chat:", error);
      toast.error(t("sidebar.failedToStartChat"));
    }
  };

  // const ASTRIA_COUNTRY_CATEGORIES = [
  //   "Astria US",
  //   "Astria Spanish",
  //   "Astria Japan",
  //   "Astria Korea",
  //   "Astria Brazil",
  //   "Astria India",
  //   "Astria PSM",
  //   "Astria Philippines",
  //   "Astria Singapore",
  //   "Astria Malaysia",
  //   "Astria Indonesia",
  //   "Astria UK",
  //   "Astria Canada",
  //   "Astria GCC",
  // ];

  // // Map user region value → the one Astria category name to show
  // const REGION_TO_ASTRIA: Record<string, string> = {
  //   indonesia: "Astria Indonesia",
  //   canada: "Astria Canada",
  //   uk: "Astria UK",
  //   gcc: "Astria GCC",
  //   malaysia: "Astria Malaysia",
  //   singapore: "Astria Singapore",
  //   philippines: "Astria Philippines",
  //   brazil: "Astria Brazil",
  //   korea: "Astria Korea",
  //   india: "Astria India",
  //   japan: "Astria Japan",
  //   spanish: "Astria Spanish",
  //   us: "Astria US",
  // };

  const userRegion = user?.region || "healjai";
  const allRegionsApproved = user?.allRegionsApproved || false;

  // Derive which categories are visible for this user's region
  // const visibleCategories = useMemo(() => {
  //   const active = categories.filter((c) => c.isActive);

  //   // Admin or "All Regions" approved → show everything
  //   if (userRegion === "all" && allRegionsApproved) return active;

  //   // Thailand (healjai) → isBussinessAstroCategoryshow all EXCEPT the Astria regional categories
  //   if (userRegion === "healjai") {
  //     return active.filter((c) => !astriaCountryCategorySet.has(c.name));
  //   }

  //   // Any specific Astria region → show that one Astria category + all shared categories
  //   const allowedAstria = REGION_TO_ASTRIA[userRegion];
  //   return active.filter(
  //     (c) => !astriaCountryCategorySet.has(c.name) || c.name === allowedAstria,
  //   );
  // }, [categories, userRegion, allRegionsApproved]);

  // Pro sees every category; a free account sees only its own region's.
  const visibleCategories = useMemo(
    () => getVisibleCategories(categories, user),
    [categories, user],
  );

  if (user?.role === "admin") return null;

  const handleSubCategorySelect = async (
    subCategoryId: string,
    categoryName: string,
  ) => {
    try {
      if (!selectedCategory) return;

      // Free plan: only Energy Match is locked; it opens the lock page.
      if (isTabLocked(user, categoryName)) {
        handleSubCategoryModalClose();
        navigate("/lock");
        return;
      }

      const isTarotCategory = categoryName === "Tarot";
      // const isUranianCategory = categoryName === "Uranian";
      const isLifeGraphCategory = categoryName === "LifeGraph";

      // Route compatibility/energy-match subcategories in country modules to the energy match form
      // Exception: Astria Japan, Korea, GCC and Indonesia Compatibility use the dedicated 3-Box UI inside the chat (sanctuary)
      const isCountryModule = ASTRIA_COUNTRY_CATEGORIES.includes(
        selectedCategory.name,
      );
      const subCategoryLower = categoryName.toLowerCase();

      // Route Marriage Verdict subcategory to the standalone marriage verdict form
      if (
        subCategoryLower.includes("marriage verdict") ||
        subCategoryLower.includes("marriage-verdict")
      ) {
        navigate("/marriage-verdict");
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }
      if (
        subCategoryLower.includes("energymatchv2") ||
        subCategoryLower.includes("energy match v2")
      ) {
        navigate(
          `/energyMatchV2?categoryId=${selectedCategory.id}&subCategoryId=${subCategoryId}`,
        );
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Route the Saju subcategory (under Astria Korea or Astria Korea V3)
      // to the standalone Saju form — separate from the normal chat thread,
      // structured JSON response.
      if (
        (selectedCategory.name === "Astria Korea" ||
          selectedCategory.name === "Astria Korea V3") &&
        subCategoryLower.includes("saju")
      ) {
        navigate(`/saju?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Route the Kyusei Viral / Timing Flow subcategories to their
      // standalone dashboard screens — the client's "Morning Check-in" flow
      // shows Omamori/Kyusei/Kippou-i (or Timing Flow) the instant the tab
      // opens, deterministically, with no chat/typing required. Matched by
      // the subcategory's own name only (not the parent category), so this
      // works identically no matter which category they're nested under
      // ("Astria Japan Talk", "Astria Japan V2", or any future container) —
      // moving them later needs no code change. Same pattern as Saju above.
      //
      // GCC V2's 7-tab dashboard is checked first (strictly scoped to the
      // "Astria GCC V2" category) so its subcategory names never fall
      // through into the Japan resolver below, which matches by
      // name-substring alone and would otherwise misroute "GCC Kyusei v2"/
      // "GCC Timing v2" into the Japan dashboard.
      const gccV2DashboardPath = resolveGCCV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (gccV2DashboardPath) {
        navigate(`${gccV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Add India V2 dashboard check here
      const indiaV2DashboardPath = resolveIndiaV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (indiaV2DashboardPath) {
        // Astria India V3 reuses V2's dashboard paths (see
        // resolveIndiaV2DashboardPath above) — the "&v=3" flag is how the
        // shared tab components know to target the V3 category/engine
        // instead of defaulting to V2.
        const indiaVersionQuery =
          selectedCategory.name === "Astria India V3" ? "&v=3" : "";
        navigate(
          `${indiaV2DashboardPath}?categoryId=${subCategoryId}${indiaVersionQuery}`,
        );
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Astria UK V2 — 9-tab dashboard, strictly scoped to "Astria UK V2" so
      // the legacy "Astria UK" (v1) category is unaffected.
      const ukV2DashboardPath = resolveUKV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (ukV2DashboardPath) {
        navigate(`${ukV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Astria Mexico — 7-tab dashboard, strictly scoped to "Astria Mexico"
      // so the existing "Astria Mexico V2" daily check-in category is
      // unaffected.
      const mexicoDashboardPath = resolveMexicoDashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (mexicoDashboardPath) {
        navigate(`${mexicoDashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      // Astria Philippines V2 / Astria Indonesia V2 — 6-tab emotional
      // check-in dashboards. Same pattern as GCC V2 / India V2 above,
      // strictly scoped by parent category name so they never collide with
      // the existing v1 "Astria Philippines" (PSM lane) / "Astria Indonesia"
      // categories.
      const philippinesV2DashboardPath = resolvePhilippinesV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (philippinesV2DashboardPath) {
        navigate(`${philippinesV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const indonesiaV2DashboardPath = resolveIndonesiaV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (indonesiaV2DashboardPath) {
        navigate(`${indonesiaV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const vietnamV2DashboardPath = resolveVietnamV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (vietnamV2DashboardPath) {
        navigate(`${vietnamV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const astriaVietnamDashboardPath = resolveAstriaVietnamDashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (astriaVietnamDashboardPath) {
        navigate(`${astriaVietnamDashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const brazilV2DashboardPath = resolveBrazilV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (brazilV2DashboardPath) {
        navigate(`${brazilV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const mexicoV2DashboardPath = resolveMexicoV2DashboardPath(
        selectedCategory.name,
        categoryName,
      );
      if (mexicoV2DashboardPath) {
        navigate(`${mexicoV2DashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const japanDashboardPath = resolveJapanDashboardPath(categoryName);
      if (japanDashboardPath) {
        navigate(`${japanDashboardPath}?categoryId=${subCategoryId}`);
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      const isCompatibilitySubcategory =
        subCategoryLower.includes("compatibility") ||
        subCategoryLower.includes("energy match") ||
        subCategoryLower.includes("energymatch");
      const isDedicatedCompatibilityRegion =
        (selectedCategory.name === "Astria Japan" ||
          selectedCategory.name === "Astria Japan V3" ||
          selectedCategory.name === "Astria Korea" ||
          selectedCategory.name === "Astria Korea V2" ||
          selectedCategory.name === "Astria Korea V3" ||
          selectedCategory.name === "Astria GCC" ||
          selectedCategory.name === "Astria Indonesia" ||
          selectedCategory.name === "Astria Malaysia V2") &&
        subCategoryLower.includes("compatibility");

      if (
        isCountryModule &&
        isCompatibilitySubcategory &&
        !isDedicatedCompatibilityRegion
      ) {
        navigate(
          `/energyMatch?country=${encodeURIComponent(selectedCategory.name)}&categoryId=${selectedCategory.id}&subCategoryId=${subCategoryId}`,
        );
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);
        if (isMobile) setIsSidebarOpen(false);
        return;
      }

      if (isTarotCategory) {
        // Navigate to tarotCard page
        // navigate(`/tarotCard?category=${subCategoryId}`);
        navigate(`/tarotCard?category=69dced23b631dcb2c1be3008`);

        // Close modal and reset state
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);

        // Close sidebar on mobile
        if (isMobile) setIsSidebarOpen(false);

        return;
      }

      // if (isUranianCategory) {
      //   // Navigate to tarotCard page
      //   navigate(`/uranian?category=${subCategoryId}`);

      //   // Close modal and reset state
      //   setShowSubCategoryModal(false);
      //   setSelectedCategory(null);
      //   setSubCategoriesForModal([]);

      //   // Close sidebar on mobile
      //   if (isMobile) setIsSidebarOpen(false);

      //   return;
      // }

      if (isLifeGraphCategory) {
        // Navigate to tarotCard page
        navigate(`/lifeGraph?category=${subCategoryId}`);

        // Close modal and reset state
        setShowSubCategoryModal(false);
        setSelectedCategory(null);
        setSubCategoriesForModal([]);

        // Close sidebar on mobile
        if (isMobile) setIsSidebarOpen(false);

        return;
      }

      // ✅ Always create a fresh chat when subcategory selected
      const newChat = await startNewChat(selectedCategory.id, subCategoryId);

      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
      setShouldAutoScroll(true);

      setShowSubCategoryModal(false);
      setSelectedCategory(null);
      setSubCategoriesForModal([]);

      if (isMobile) setIsSidebarOpen(false);
    } catch (error) {
      console.error("Failed to start new chat with subcategory:", error);
      toast.error(t("sidebar.failedToStartChat"));
    }
  };

  const handleSubCategoryModalClose = () => {
    setShowSubCategoryModal(false);
    setSelectedCategory(null);
    setSubCategoriesForModal([]);
  };

  const applyTesterBranding = (label: string) => {
    if (!isTester || !label) return label;
    return label.replace(/Astria/gi, "Cosmic");
  };

  const getSubCategoryLabel = (subCategoryId?: string | null) => {
    if (!subCategoryId) return "Other";
    const sc = subCategories.find((s) => s.id === subCategoryId);
    if (!sc) return "Other";
    return sc.name_en;
  };
  const getSubCategoryLabelTwo = (subCategoryId?: string | null) => {
    if (!subCategoryId) return "Other";
    const sc = subCategories.find((s) => s.id === subCategoryId);
    if (!sc) return "Other";
    return localizedField(sc, "name", myLang);
  };

  const getFilteredChats = (chatList: any[]) => {
    if (!searchTerm.trim()) return chatList;
    const term = searchTerm.toLowerCase().trim();
    return chatList.filter(
      (chat) =>
        chat.title.toLowerCase().includes(term) ||
        chat.messages?.some((m) => m.content?.toLowerCase().includes(term)),
    );
  };

  const handleChatSelect = async (chatId: string) => {
    try {
      navigate(`/sanctuary?chat=${chatId}`);

      if (chatId.startsWith("t-")) {
        const chat = chats.find((c) => c.id === chatId);
        if (chat) {
          setCurrentChat(chat);
          setShouldAutoScroll(true);
        }
      } else {
        const chat = await fetchSingleChat(chatId);
        if (chat) {
          setCurrentChat(chat);
          setShouldAutoScroll(true);
        }
      }

      if (isMobile) {
        setIsSidebarOpen(false);
        dispatchSidebarState(false);
      }
    } catch (error) {
      console.error("Failed to load chat:", error);
      toast.error(t("sidebar.failedToLoadChat"));
    }
  };

  const handleDeleteClick = (
    chatId: string,
    chatTitle: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setChatToDelete({ id: chatId, title: chatTitle });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (chatToDelete) {
      const success = await deleteChat(chatToDelete.id);
      if (success) {
        setDeleteModalOpen(false);
        setChatToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setChatToDelete(null);
  };

  const getChatsByCategory = (categoryId: string) => {
    const categoryChats = chats
      .filter((chat) => chat.categoryId === categoryId)
      .sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt).getTime() -
          new Date(a.updatedAt || a.createdAt).getTime(),
      );

    return getFilteredChats(categoryChats);
  };

  const getSubCategoryGroups = (categoryId: string, list: any[]) => {
    const searchActive = !!searchTerm.trim();
    const subCatsForCategory = subCategories.filter(
      (sc) => sc.categoryId === categoryId && sc.isActive,
    );
    const subCatIdsFromChats = Array.from(
      new Set(list.filter((c) => c.subCategoryId).map((c) => c.subCategoryId)),
    );
    const allSubCatIds = new Set<string>([
      ...subCatIdsFromChats,
      ...subCatsForCategory.map((sc) => sc.id),
    ]);

    const groups = Array.from(allSubCatIds)
      .map((id) => {
        const chatsForSub = list.filter((chat) => chat.subCategoryId === id);
        const label = getSubCategoryLabel(id);
        let label_th = "";
        let label_es = "";
        let label_id = "";
        let label_ar = "";
        if (myLang === "th") {
          label_th = getSubCategoryLabelTwo(id);
        } else if (myLang === "es") {
          label_es = getSubCategoryLabelTwo(id);
        } else if (myLang === "id") {
          label_id = getSubCategoryLabelTwo(id);
        } else if (myLang === "ar") {
          label_ar = getSubCategoryLabelTwo(id);
        }
        return {
          key: `sub:${id}`,
          label,
          label_th,
          label_es,
          label_id,
          label_ar,
          chats: chatsForSub,
        };
      })
      .filter((group) => {
        const name = group.label?.toString().toLowerCase();
        return name !== "lifegraph" && name !== "tarot";
      });

    const visibleGroups = searchActive
      ? groups.filter((g) => g.chats.length > 0)
      : groups;

    return visibleGroups;
  };

  const hasSubCategoryData = (categoryId: string, list: any[]) =>
    subCategories.some((sc) => sc.categoryId === categoryId && sc.isActive) ||
    list.some((chat) => chat.subCategoryId);

  const getGeneralChats = () => {
    const generalChats = chats
      .filter((chat) => !chat.categoryId)
      .sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt).getTime() -
          new Date(a.updatedAt || a.createdAt).getTime(),
      );

    return getFilteredChats(generalChats);
  };

  const isCategoryHighlighted = (categoryId: string) =>
    currentChat?.categoryId === categoryId;

  const dispatchSidebarState = (isOpen: boolean) => {
    if (isMobile) {
      window.dispatchEvent(
        new CustomEvent("sidebarStateChange", {
          detail: { isOpen },
        }),
      );
    }
  };

  const toggleSidebar = () => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    dispatchSidebarState(newState);
  };

  const handleLogoClick = () => {
    setCurrentChat(null); // ✅ clear stale chat
    setShowSubCategoryModal(false);
    setSelectedCategory(null);
    setSubCategoriesForModal([]);
    navigate("/");
    if (isMobile) {
      setIsSidebarOpen(false);
      dispatchSidebarState(false);
    }
  };

  return (
    <>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("sidebar.deleteChat")}
        message={t("sidebar.deleteConfirmation")}
        itemName={chatToDelete?.title}
      />

      <SubCategorySelectionModal
        isOpen={showSubCategoryModal}
        onClose={handleSubCategoryModalClose}
        onSelect={handleSubCategorySelect}
        categoryName={applyTesterBranding(selectedCategory?.name || "")}
        subCategories={subCategoriesForModal}
      />

      {/* ✅ Mobile open button */}
      {isMobile && !isSidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="fixed top-4 left-4 z-50 p-2 rounded-xl border shadow-lg backdrop-blur transition hover:scale-105"
          style={{
            background: isLight ? "linear-gradient(135deg, #2D1B44, #6D4C92)" : "var(--astria-surface)",
            borderColor: "rgba(167,139,255,0.35)",
            boxShadow: "0 0 18px var(--astria-accent-glow)",
          }}
        >
          <Menu className="w-6 h-6 text-[#A78BFF]" />
        </button>
      )}

      {/* ✅ Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={() => {
            setIsSidebarOpen(false);
            dispatchSidebarState(false);
          }}
        />
      )}

      <aside
        className={`
          fixed md:relative top-0 left-0 h-screen z-40
          transition-all duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0 w-72" : "-translate-x-full md:translate-x-0 md:w-20"}
          overflow-hidden
          text-[#C8BFD1]
        `}
        style={{
          background: isTester
            ? `linear-gradient(180deg,
            #2E3A46 0%,
            #24303A 45%,
            #1A242C 85%,
            #10171D 100%
          )`
            : isLight
              ? `linear-gradient(180deg,
            #8663d1ff 0%,
            #5736a6ff 45%,
            #3a1662ff 85%,
            #1c0a33ff 100%
          )`
              : "var(--astria-surface)",
          ...(isLight ? lightSidebarVars : {}),
        }}
      >
        <div className="h-full flex flex-col relative overflow-hidden">
          {/* ✅ Top Header */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between flex-shrink-0 z-10">
            {isSidebarOpen || isMobile ? (
              <>
                {isTester ? (
                  <div
                    className="font-bold text-white text-xl tracking-tight cursor-pointer"
                    style={{ color: colors.roseGold }}
                    onClick={handleLogoClick}
                  >
                    {t("astriaHome.nav.testerMode")}
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={handleLogoClick}
                  >
                    <div className="w-11 h-11 flex items-center justify-center">
                      <AstriaRingLogo className="w-9 h-9" alt="Astria Logo" />
                    </div>

                    <div
                      className="font-bold text-white text-xl tracking-tight"
                      style={{ color: colors.roseGold }}
                    >
                      Astria
                    </div>
                  </div>
                )}

                {!isTester && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMainPlusClick()}
                      className="p-2 rounded-xl hover:bg-white/10 text-white/80"
                      title={t("sidebar.newChat")}
                    >
                      <Plus
                        className="w-5 h-5"
                        style={{ color: colors.roseGold }}
                      />
                    </button>

                    <button
                      onClick={toggleSidebar}
                      className="p-2 rounded-xl hover:bg-white/10 text-white/80"
                      title={
                        isSidebarOpen
                          ? t("sidebar.collapse")
                          : t("sidebar.expand")
                      }
                      style={{ color: colors.roseGold }}
                    >
                      {isSidebarOpen ? (
                        <PanelLeftClose className="w-5 h-5" />
                      ) : (
                        <PanelLeftOpen className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full flex justify-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-xl hover:bg-white/10 text-white/80"
                  title={t("sidebar.expand")}
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>

          {(isSidebarOpen || isMobile) && (
            <div className="px-5 pb-3 flex-shrink-0">
              <div className="h-px bg-white/10" />
            </div>
          )}

          {/* ✅ Main content */}
          {isSidebarOpen || isMobile ? (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Astria nav: five places + account. Pinned, so it never scrolls away. */}
              {!isTester && isAuthenticated && (
                <div className="flex-shrink-0 px-4 pb-1">
                  <AstriaNav compact={showHistory} />
                  <button
                    type="button"
                    onClick={() => setShowHistory((v) => !v)}
                    aria-expanded={showHistory}
                    className="flex w-full items-center justify-between px-5 py-2.5 text-[15px] text-astria-muted transition hover:bg-white/5"
                    style={{ borderRadius: 12 }}
                  >
                    <span>{astriaCopy("navHistory")}</span>
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${showHistory ? "rotate-90" : ""}`}
                    />
                  </button>
                </div>
              )}
              <div
                ref={scrollContainerRef}
                className="astria-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 pb-6 space-y-5"
              >

                {/* Older content (quote, astrology card, topics, chats) lives under History */}
                <div
                  className={
                    !isTester && isAuthenticated && !showHistory
                      ? "hidden"
                      : "space-y-5"
                  }
                >
                {/* ✅ Your Region — mirrors the highlighted card in the
                    Worldwide section on the home page */}
                {isAuthenticated && (isSidebarOpen || isMobile) && (
                  <button
                    onClick={() => navigate("/home")}
                    className="w-full mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-white/10"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: `1px solid ${colors.roseGold}55`,
                    }}
                    title={getRegionLabel(userRegion)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-white/45">
                        {t("sidebar.yourRegion")}
                      </div>
                      <div
                        className="text-sm font-semibold truncate"
                        style={{ color: colors.roseGold }}
                      >
                        {getRegionLabel(userRegion)}
                      </div>
                    </div>
                  </button>
                )}

                {/* ✅ Categories list — filtered by user's region */}
                {isAuthenticated &&
                  !categoriesFetched &&
                  categoriesLoading &&
                  visibleCategories.length === 0 && (
                    <div className="flex items-center gap-2 px-1 py-3 text-white/50 text-sm">
                      <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
                      {t("Loading Categories...") || "Loading categories..."}
                    </div>
                  )}

                <div className="space-y-2">
                  {visibleCategories.map((cat) => {
                    const isHighlighted = isCategoryHighlighted(cat.id);
                    const list = getChatsByCategory(cat.id);
                    const shouldGroupBySubCategory = hasSubCategoryData(
                      cat.id,
                      list,
                    );
                    const subCategoryGroups = shouldGroupBySubCategory
                      ? getSubCategoryGroups(cat.id, list)
                      : [];

                    return (
                      <div
                        key={cat.id}
                        className={[
                          "rounded-xl transition-all",
                          isHighlighted
                            ? "bg-white/10 border border-white/10 shadow-lg"
                            : "",
                        ].join(" ")}
                      >
                        <div className="flex items-center">
                          <button
                            onClick={() =>
                              setExpandedCategories((prev) => ({
                                ...prev,
                                [cat.id]: !prev[cat.id],
                              }))
                            }
                            className="pl-3 py-3 pr-1 flex items-center justify-center flex-shrink-0 hover:bg-white/5 rounded-l-xl transition"
                            title={
                              expandedCategories[cat.id]
                                ? t("sidebar.collapse")
                                : t("sidebar.expand")
                            }
                          >
                            {expandedCategories[cat.id] ? (
                              <ChevronDown className="h-4 w-4 text-white/50" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-white/50" />
                            )}
                          </button>

                          <button
                            onClick={() => handleNewChat(cat.id)}
                            className="flex-1 flex items-center gap-3 pr-3 py-3 text-left group rounded-r-xl hover:bg-white/5 transition"
                            title={applyTesterBranding(
                              localizedField(cat, "name", myLang),
                            )}
                          >
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ color: colors.roseGold }}
                            >
                              {(() => {
                                const Icon = getCategoryIcon(cat.icon);
                                return (
                                  <Icon
                                    size={16}
                                    style={{ color: colors.roseGold }}
                                  />
                                );
                              })()}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium group-hover:text-white transition-colors truncate">
                                {applyTesterBranding(
                                  localizedField(cat, "name", myLang),
                                )}
                              </div>
                              {cat.name !== "Healing Horoscope" && (
                                <div className="text-[11px] text-white/35">
                                  {list.length} {t("sidebar.chats")}
                                </div>
                              )}
                            </div>

                            <Plus className="w-4 h-4 text-white/30 group-hover:text-white/60 transition" />
                          </button>
                        </div>

                        {/* Chats under category */}
                        {list.length > 0 &&
                          expandedCategories[cat.id] === true && (
                            <div className="pb-3">
                              {shouldGroupBySubCategory ? (
                                <div className="ml-11 mr-2 space-y-2 pr-1">
                                  {subCategoryGroups.map((group) => {
                                    const isExpanded =
                                      expandedSubCategories[group.key];
                                    const canScroll = group.chats.length >= 4;

                                    return (
                                      <div key={group.key}>
                                        <button
                                          onClick={() =>
                                            setExpandedSubCategories(
                                              (prev) => ({
                                                ...prev,
                                                [group.key]: !prev[group.key],
                                              }),
                                            )
                                          }
                                          className="w-full flex items-start gap-2 rounded-xl px-3 py-2 text-left text-white/75 transition hover:bg-white/5"
                                        >
                                          <div className="mt-0.5">
                                            {isExpanded ? (
                                              <ChevronDown className="h-4 w-4 text-white/50" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4 text-white/50" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">
                                              {myLang === "en"
                                                ? group.label
                                                : myLang === "th"
                                                  ? group.label_th
                                                  : myLang === "es"
                                                    ? group.label_es
                                                    : myLang === "id"
                                                      ? group.label_id
                                                      : myLang === "ar"
                                                        ? group.label_ar
                                                        : group.label}
                                              {/* {group.label} */}
                                            </div>
                                            <div className="text-[11px] text-white/35">
                                              {group.chats.length}{" "}
                                              {t("sidebar.chats")}
                                            </div>
                                          </div>
                                        </button>

                                        {isExpanded &&
                                          group.chats.length > 0 && (
                                            <div
                                              className={`ml-6 mt-1 space-y-1 ${
                                                canScroll
                                                  ? "h-[160px] overflow-y-auto hide-scrollbar pr-1"
                                                  : ""
                                              }`}
                                            >
                                              {group.chats.map((chat) => {
                                                const isActive =
                                                  currentChat?.id === chat.id;

                                                return (
                                                  <div
                                                    key={chat.id}
                                                    className="group/item relative"
                                                    ref={
                                                      isActive
                                                        ? activeChatRef
                                                        : null
                                                    }
                                                  >
                                                    <button
                                                      onClick={() =>
                                                        handleChatSelect(
                                                          chat.id,
                                                        )
                                                      }
                                                      className={[
                                                        "w-full text-left pl-3 pr-7 py-2 rounded-xl transition",
                                                        isActive
                                                          ? "bg-white/10 text-white border border-white/10"
                                                          : "text-white/45 hover:bg-white/5 hover:text-white/75",
                                                      ].join(" ")}
                                                    >
                                                      <div className="text-sm truncate">
                                                        {chat.title}
                                                      </div>
                                                    </button>

                                                    <button
                                                      onClick={(e) =>
                                                        handleDeleteClick(
                                                          chat.id,
                                                          chat.title,
                                                          e,
                                                        )
                                                      }
                                                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover/item:opacity-100 hover:bg-white/10 text-red-300/70 hover:text-red-300 transition"
                                                      title={t(
                                                        "sidebar.delete",
                                                      )}
                                                    >
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div
                                  className={`ml-11 mr-2 space-y-1 pr-1 ${
                                    list.length >= 4
                                      ? "h-[160px] overflow-y-auto hide-scrollbar"
                                      : ""
                                  }`}
                                >
                                  {list.map((chat) => {
                                    const isActive =
                                      currentChat?.id === chat.id;

                                    return (
                                      <div
                                        key={chat.id}
                                        className="group/item relative"
                                        ref={isActive ? activeChatRef : null}
                                      >
                                        <button
                                          onClick={() =>
                                            handleChatSelect(chat.id)
                                          }
                                          className={[
                                            "w-full text-left pl-3 pr-7 py-2 rounded-xl transition",
                                            isActive
                                              ? "bg-white/10 text-white border border-white/10"
                                              : "text-white/45 hover:bg-white/5 hover:text-white/75",
                                          ].join(" ")}
                                        >
                                          <div className="text-sm truncate">
                                            {chat.title}
                                          </div>
                                          {chat.subCategoryId && (
                                            <div className="mt-0.5 text-[11px] text-white/35">
                                              {getSubCategoryLabel(
                                                chat.subCategoryId,
                                              )}
                                            </div>
                                          )}
                                        </button>

                                        <button
                                          onClick={(e) =>
                                            handleDeleteClick(
                                              chat.id,
                                              chat.title,
                                              e,
                                            )
                                          }
                                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover/item:opacity-100 hover:bg-white/10 text-red-300/70 hover:text-red-300 transition"
                                          title={t("sidebar.delete")}
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>

                {/* <div className="h-px bg-white/10" /> */}

                {/* ✅ Conversations header */}
                {/* <div className="text-[11px] font-bold text-white/30 uppercase tracking-widest px-1">
                  {t('sidebar.conversations')}
                </div> */}

                {/* ✅ Search */}
                {/* <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('sidebar.search')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/20"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "#fff",
                    }}
                  />
                </div> */}

                {/* ✅ General chats */}
                <div className="space-y-1">
                  {getGeneralChats()
                    .slice(0, 50)
                    .map((chat) => {
                      const isActive = currentChat?.id === chat.id;

                      return (
                        <div
                          key={chat.id}
                          className="group/item relative"
                          ref={isActive ? activeChatRef : null}
                        >
                          <button
                            onClick={() => handleChatSelect(chat.id)}
                            className={[
                              "w-full text-left pl-3 pr-7 py-2 rounded-xl transition border",
                              isActive
                                ? "bg-white/10 text-white border-white/10"
                                : "bg-transparent text-white/45 border-transparent hover:bg-white/5 hover:text-white/75",
                            ].join(" ")}
                          >
                            <div className="text-sm truncate">{chat.title}</div>
                          </button>

                          <button
                            onClick={(e) =>
                              handleDeleteClick(chat.id, chat.title, e)
                            }
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover/item:opacity-100 hover:bg-white/10 text-red-300/70 hover:text-red-300 transition"
                            title={t("sidebar.delete")}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                </div>
                </div>
              </div>
            </div>
          ) : (
            // ✅ Collapsed sidebar view (icons)
            <div className="astria-scroll flex-1 min-h-0 px-2 pb-3 overflow-y-auto overscroll-contain">
              {!isTester && isAuthenticated && <AstriaNav collapsed />}
              <div className="pt-3 flex flex-col items-center gap-2">
                {getGeneralChats()
                  .slice(0, 6)
                  .map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => handleChatSelect(chat.id)}
                      className="w-11 h-11 rounded-xl hover:bg-white/10 flex items-center justify-center"
                      title={chat.title}
                    >
                      <MessageSquare className="w-5 h-5 text-white/70" />
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* ✅ User footer */}
          <div className="px-4 py-2 border-t border-white/10 bg-black/10 flex-shrink-0">
            {!isSidebarOpen && !isMobile ? (
              <button
                className="w-full flex items-center justify-center p-2 rounded-xl hover:bg-white/5 transition-colors"
                onClick={
                  user ? logout : () => (window.location.href = "/login")
                }
                title={user ? t("sidebar.account") : t("sidebar.logIn")}
              >
                <div className="w-10 h-10 flex-shrink-0 aspect-square rounded-full border border-white/15 flex items-center justify-center bg-gradient-to-br from-white/10 to-transparent">
                  <span
                    className="text-sm font-bold"
                    style={{ color: colors.roseGold }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
              </button>
            ) : (isSidebarOpen || isMobile) && user ? (
              <button
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                onClick={logout}
              >
                <div className="w-10 h-10 flex-shrink-0 aspect-square rounded-full border border-white/15 flex items-center justify-center bg-gradient-to-br from-white/10 to-transparent">
                  <span
                    className="text-sm font-bold"
                    style={{ color: colors.roseGold }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>

                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {user?.username || t("sidebar.signIn")}
                  </div>
                  <div className="text-[10px] text-white/35 uppercase tracking-tight">
                    {t("sidebar.account")}
                  </div>
                </div>

                <LogOut className="w-4 h-4 text-white/40 group-hover:text-red-300 transition-colors" />
              </button>
            ) : (
              <button
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                onClick={() => (window.location.href = "/login")}
              >
                <div className="w-10 h-10 flex-shrink-0 aspect-square rounded-full border border-white/15 flex items-center justify-center bg-gradient-to-br from-white/10 to-transparent">
                  <span
                    className="text-sm font-bold"
                    style={{ color: colors.roseGold }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>

                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {t("sidebar.logIn")}
                  </div>
                </div>

                <LogIn className="w-4 h-4 text-white/40 group-hover:text-red-300 transition-colors" />
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
